import { Dispatch } from 'redux';

import { CondominiumsPageFilter } from '@portal/models/advanced-filters';

import CondominiumsApi from '@portal/repositories/condominiums';
import * as MessageService from '~/services/message';
import * as StorageService from '~/services/storage';
import jwt_decode from 'jwt-decode';

import { decreaseLoading, increaseLoading } from './loading';
import {
  CONDOMINIUMS_LIST,
  CONDOMINIUMS_UPLOAD_LIST,
  CONDOMINIUMS_UPLOAD_STATUS,
  CLEAN_UPLOAD_STATUS_CONDOMINIUMS,
  SET_PAYMENT_ACCOUNT_ID,
  CONDOMINIUM_IS_LOGGED,
} from './actionTypes';
import { UploadStatusEnum } from '@portal/models/enumerators/condominiums';
import { User } from '@portal/models/module';
import { setHeader } from '@portal/repositories/securitySdkConfig';

export const getCondominiums =
  (searchParams: CondominiumsPageFilter) => async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await CondominiumsApi.getCondominiums({
        ...(searchParams
          ? {
              ...searchParams,
              offset: searchParams.page,
              limit: searchParams.pageSize,
            }
          : {
              pageSize: 10,
              page: 1,
              orderBy: 'createdAt',
              sort: 'desc',
            }),
      });

      dispatch({
        payload,
        type: CONDOMINIUMS_LIST,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getUploadLogList =
  (params?: CondominiumsPageFilter) => async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await CondominiumsApi.getUploadLogList({
        ...(params
          ? {
              ...params,
              offset: params.page,
              limit: params.pageSize,
            }
          : {
              pageSize: 10,
              page: 1,
              orderBy: 'createdAt',
              sort: 'desc',
            }),
      });

      dispatch({
        payload,
        type: CONDOMINIUMS_UPLOAD_LIST,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const uploadCondominiumsList =
  (file: File, fileExtension: string, fileName: string, accountId: string) =>
  async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const condominiumsBucket = await CondominiumsApi.getUrl({
        fileExtension,
        fileName,
      });

      await CondominiumsApi.uploadFile(
        file,
        fileExtension,
        condominiumsBucket.url
      );

      const completeParams = {
        bucketPath: condominiumsBucket.bucket,
        preview: false,
      };

      await CondominiumsApi.getPreview(completeParams, accountId);

      dispatch({
        payload: UploadStatusEnum.CONCLUDED,
        type: CONDOMINIUMS_UPLOAD_STATUS,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch({
          type: CONDOMINIUMS_UPLOAD_STATUS,
          payload: UploadStatusEnum.ERROR,
        });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanUploadStatusCondominiums =
  () => async (dispatch: Dispatch) => {
    dispatch({
      type: CLEAN_UPLOAD_STATUS_CONDOMINIUMS,
    });
  };

export const AccountSwitch =
  (
    documentNumber: string,
    authUser: User,
    callback: (err: Error | null) => void
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await CondominiumsApi.AccountSwitch(documentNumber);

      StorageService.setItem('session-token', JSON.stringify(payload));
      setHeader(
        StorageService.StorageItems.AUTHORIZATION,
        `Bearer ${payload.access_token}`
      );

      const accessJwt: models.Jwt = jwt_decode(payload.access_token);

      const publicKey = StorageService.getItem(`publicKey_${authUser.id}`);
      const privateKey = StorageService.getItem(`privateKey_${authUser.id}`);

      const isAuthUserSet = StorageService.getItem('auth-user-admin');

      if (!isAuthUserSet) {
        StorageService.setItem('auth-user-admin', JSON.stringify(authUser));
      }

      StorageService.setItem(`publicKey_${accessJwt.userId}`, publicKey ?? '');
      StorageService.setItem(
        `privateKey_${accessJwt.userId}`,
        privateKey ?? ''
      );

      const paymentAccountId = {
        id: payload.paymentAccount.id,
      };

      if (paymentAccountId.id) {
        setHeader(
          StorageService.StorageItems.PAYMENT_ACCOUNT_ID,
          paymentAccountId.id,
          'session'
        );
      }

      dispatch({
        type: SET_PAYMENT_ACCOUNT_ID,
        payload: paymentAccountId,
      });

      dispatch({
        type: CONDOMINIUM_IS_LOGGED,
        payload: true,
      });

      callback(null);
    } catch (err) {
      if (err instanceof Error) {
        callback(err);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
